import { ALL_FORMATS, BlobSource, CanvasSink, Input } from "mediabunny";

export interface FrameThumbnailer {
  /** Decode the frame nearest `time` (seconds) into a canvas; null if none there. */
  frameAt(time: number): Promise<HTMLCanvasElement | OffscreenCanvas | null>;
  /** Release the underlying input. Call when done. */
  dispose(): void;
}

/**
 * Build a reusable frame extractor for a file via mediabunny's `CanvasSink`.
 * Seeks to arbitrary timestamps and decodes downscaled frames (e.g. for in/out
 * thumbnails) without touching any `<video>` element. Returns `null` if the
 * file has no decodable video track. Browser-only (WebCodecs).
 */
export async function createFrameThumbnailer(
  file: Blob,
  options: { width?: number } = {}
): Promise<FrameThumbnailer | null> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track || !(await track.canDecode())) {
      input.dispose();
      return null;
    }
    const sink = new CanvasSink(track, { width: options.width ?? 240 });
    return {
      frameAt: async (time) => (await sink.getCanvas(time))?.canvas ?? null,
      dispose: () => input.dispose(),
    };
  } catch {
    input.dispose();
    return null;
  }
}
